import React, { useEffect, useMemo, useState } from "react";
import { useGetFieldsetKeyMappings, useGetFieldsets } from "./customhooks";
import { Button, Icon, PageDimmer } from "../../../components/tailwind";
import { useGetCustomerQuery } from "../../../api/customerApi";
import { Text } from "../../../components/Text";
import { FieldsetForm } from "./FieldsetForm";
import { FieldSchemaToggleList } from "./FieldSchemaToggleList";
import { ListFieldsets } from "./ListFieldsets";
import { Fieldset, Field } from "./types";
import { useUrlState } from "../../../utils/react-custom-hooks/urlStateHandler";
import ManageGrid from "../../../components/manage/ManageGrid";
import { FieldsetMappingsForm } from "./FieldsetMappingsForm";
import { useGetDocumentSections } from "../../../planner/document-structure/manage/customhooks";

export type BooleanFilterType = "true" | "false";
const Views = {
  IS: "input-schema",
  MAPPINGS: "mappings",
} as const;

type ViewsValues = typeof Views[keyof typeof Views];

export const Fieldsets: React.FC = () => {
  const { setParam, urlState, removeParam } = useUrlState<{
    fieldset_id: number;
    view: ViewsValues;
    m_search: string;
    m_list_search: string;
    m_filter_mappings: BooleanFilterType;
  }>();
  const {
    fieldset_id,
    view,
    m_search,
    m_list_search,
    m_filter_mappings,
  } = urlState;
  const [showForm, setShowForm] = useState(false);
  const [editModeField, setEditModeField] = useState(false);
  const [selectedField, setSelectedField] = useState<Field | undefined>();

  useEffect(() => {
    if (fieldset_id && !showForm) {
      setShowForm(true);
    }
  }, [fieldset_id]);

  useEffect(() => {
    if (!editModeField) {
      setSelectedField(undefined);
    }
  }, [editModeField]);

  const setSelectedFieldset = (fieldset: Fieldset): void => {
    if (!fieldset) {
      removeParam("fieldset_id");
      setShowForm(false);
      return;
    }
    setShowForm(true);
    setParam("fieldset_id", fieldset.id);
  };

  const setListSearch = (value: string): void => {
    if (!value) {
      removeParam("m_list_search");
      return;
    }
    setParam("m_list_search", value);
  };

  const {
    data: { fieldsets, known_keys: knownKeys },
    isFetching: isFetchingFieldsets,
    reFetch: reFetchFieldsets,
  } = useGetFieldsets({ includeKnownKeys: true });

  const {
    data: sections,
    isFetching: isFetchingSections,
  } = useGetDocumentSections();

  const {
    data: keyMappings,
    isFetching: isFetchingFieldsetKeyMappings,
    reFetch: reFetchFieldsetKeyMappings,
  } = useGetFieldsetKeyMappings();

  const { data: customer } = useGetCustomerQuery();

  const [isCopying, setIsCopying] = useState(false);

  const defaultFieldset = useMemo(() => {
    const defaultFieldset = fieldsets.find(
      ({ use_as_default }) => use_as_default
    );
    if (defaultFieldset) {
      return defaultFieldset;
    }
    const createdDefault: Fieldset = {
      id: 0,
      customer_id: 0,
      name: "Default",
      created: new Date(),
      fields: knownKeys.map(({ key }) => ({ key })),
      use_as_default: true,
    };
    return createdDefault;
  }, [fieldsets, knownKeys]);

  const fields = useMemo((): Field[] => {
    const dataFields: Field[] = knownKeys.map(({ key, name }) => ({
      name: name ?? key,
      key,
    }));
    const sectionFields: Field[] = (sections ?? [])
      .filter(({ template_label_ids }) => !template_label_ids)
      .map(({ name, id }) => ({ name, section_id: id }));
    return [...dataFields, ...sectionFields];
  }, [knownKeys, sections]);

  const selectedFieldset = useMemo(() => {
    if (fieldset_id === 0) {
      return defaultFieldset;
    }
    return [...fieldsets, defaultFieldset].find(({ id }) => id === fieldset_id);
  }, [fieldset_id, fieldsets, defaultFieldset]);

  const displayName = useMemo(
    () =>
      selectedFieldset?.name.length > 60
        ? `${selectedFieldset?.name.substring(0, 60)}...`
        : selectedFieldset?.name,
    [selectedFieldset]
  );

  const isViewingMappings = view == Views.MAPPINGS;

  return (
    <div className="manage-content">
      <ManageGrid>
        <ManageGrid.Header>
          <ManageGrid.Header.LeftColumn
            left={
              <div>
                {editModeField ? (
                  <span
                    data-balloon="Turn schemas on or off per field (settings are stored equivalently)."
                    data-balloon-pos="down-left"
                    data-balloon-length="medium"
                  >
                    <Text
                      as="h4"
                      compact
                      className="tw-flex tw-gap-2 tw-items-center"
                    >
                      PIM fields
                      <Icon name="help" className="tw-text-xl" />
                    </Text>
                  </span>
                ) : (
                  !isViewingMappings && (
                    <span
                      data-balloon="Schemas determine which parts of your product's data are used when generating descriptions."
                      data-balloon-pos="down-left"
                      data-balloon-length="medium"
                    >
                      <Text
                        as="h4"
                        compact
                        className="tw-flex tw-gap-2 tw-items-center"
                      >
                        Schemas
                        <Icon name="help" className="tw-text-xl" />
                      </Text>
                    </span>
                  )
                )}
                {!isViewingMappings && (
                  <div className="tw-mt-2">
                    <Button
                      data-testid="fieldset-mode-schema"
                      onClick={(): void => setEditModeField(false)}
                      className="!tw-rounded-r-none"
                      variant={
                        editModeField ? "textual-blue-alt" : "textual-blue"
                      }
                      compact="very"
                    >
                      Schema
                    </Button>
                    <Button
                      data-testid="fieldset-mode-field"
                      onClick={(): void => setEditModeField(true)}
                      className="!tw-rounded-l-none"
                      variant={
                        !editModeField ? "textual-blue-alt" : "textual-blue"
                      }
                      compact="very"
                    >
                      Field
                    </Button>
                  </div>
                )}
              </div>
            }
            right={
              !editModeField &&
              !isViewingMappings && (
                <span
                  data-balloon="Create new Input Schema"
                  data-balloon-pos="left"
                >
                  <Button
                    data-testid="create-fieldset"
                    variant="secondary-alt"
                    icon
                    compact
                    onClick={(): void => {
                      setIsCopying(false);
                      setSelectedFieldset(undefined);
                      setShowForm(true);
                    }}
                  >
                    <Icon name="add" className="tw-text-lg tw-leading-[1.25]" />
                  </Button>
                </span>
              )
            }
          />

          <ManageGrid.Header.RightColumn>
            <div className="tw-flex tw-items-center tw-gap-2">
              {!isViewingMappings ? (
                <>
                  {isCopying && (
                    <Text
                      compact
                      as="h3"
                      testId="fieldset-selected-action-copy"
                      inline
                    >
                      Copying schema: {displayName}
                    </Text>
                  )}
                  {!!selectedFieldset && !isCopying && !editModeField && (
                    <Text
                      compact
                      as="h3"
                      testId="fieldset-selected-action-update"
                      inline
                    >
                      Update: {displayName}
                    </Text>
                  )}
                  {editModeField && selectedField && (
                    <Text
                      compact
                      as="h3"
                      testId="fieldset-selected-action-update-pim"
                      inline
                    >
                      Update: {selectedField.name ?? selectedField.key ?? ""}
                    </Text>
                  )}
                  {!selectedFieldset && showForm && !editModeField && (
                    <Text
                      compact
                      as="h3"
                      testId="fieldset-selected-action-create"
                      inline
                    >
                      New schema
                    </Text>
                  )}
                </>
              ) : (
                <Text compact as="h3" testId="fieldset-mappings-view" inline>
                  Modify field names ({Object.keys(keyMappings).length})
                </Text>
              )}

              <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
                <Button
                  data-testid="fieldset-open-pim-fields"
                  variant="primary-alt"
                  compact
                  icon
                  onClick={(): void => {
                    const slug = customer?.slug || "_";
                    window.open(`/d/${slug}/manage/customer-fields`, "_blank");
                  }}
                >
                  <Icon name="open_in_new" />
                  PIM fields
                </Button>
                <Button
                  data-testid="fieldset-change-view"
                  variant={isViewingMappings ? "primary" : "primary-alt"}
                  compact
                  onClick={(): void => {
                    setParam(
                      "view",
                      isViewingMappings ? Views.IS : Views.MAPPINGS
                    );
                  }}
                >
                  Modify field names
                </Button>
              </div>
            </div>
          </ManageGrid.Header.RightColumn>
        </ManageGrid.Header>
        <ManageGrid.Body>
          <ManageGrid.Body.List>
            {!isViewingMappings && (
              <ListFieldsets
                defaultFieldset={defaultFieldset}
                fieldsets={fieldsets.filter(
                  ({ use_as_default }) => !use_as_default
                )}
                selectedFieldset={selectedFieldset}
                setSelectedFieldset={setSelectedFieldset}
                setIsCopying={(isCopying): void => {
                  setIsCopying(isCopying);
                  setShowForm(true);
                }}
                isCopying={isCopying}
                editModeField={editModeField}
                fields={fields}
                selectedField={selectedField}
                setSelectedField={(field): void => {
                  setSelectedField(field);
                  setShowForm(true);
                }}
                search={m_list_search ?? ""}
                setSearch={setListSearch}
              />
            )}
          </ManageGrid.Body.List>

          <ManageGrid.Body.Form>
            {isViewingMappings && (
              <FieldsetMappingsForm
                keyMappings={keyMappings}
                knownKeys={knownKeys}
                loading={isFetchingFieldsetKeyMappings}
                reFetch={reFetchFieldsetKeyMappings}
                search={m_search}
                setSearch={(search?: string): void => {
                  if (!search) {
                    removeParam("m_search");
                    return;
                  }
                  setParam("m_search", search);
                }}
                filterMappings={m_filter_mappings}
                setFilterMappings={(filter?: BooleanFilterType): void => {
                  if (!filter || filter === m_filter_mappings) {
                    removeParam("m_filter_mappings");
                    return;
                  }
                  setParam("m_filter_mappings", filter);
                }}
              />
            )}
            {!isViewingMappings && editModeField && selectedField && (
              <FieldSchemaToggleList
                field={selectedField}
                fieldsets={[
                  defaultFieldset,
                  ...fieldsets.filter(({ use_as_default }) => !use_as_default),
                ]}
                refetchFieldsets={reFetchFieldsets}
              />
            )}
            {!isViewingMappings && editModeField && !selectedField && (
              <ManageGrid.Body.EmptyForm>
                <Text as="h2" compact>
                  Select a field from the list
                </Text>
              </ManageGrid.Body.EmptyForm>
            )}
            {!isViewingMappings && !editModeField && showForm && (
              <FieldsetForm
                loading={isFetchingFieldsets}
                knownKeys={knownKeys}
                refetchFieldsets={reFetchFieldsets}
                selectedFieldset={selectedFieldset}
                setSelectedFieldset={setSelectedFieldset}
                setIsCopying={setIsCopying}
                isCopying={isCopying}
                sections={sections}
                isFetchingSections={isFetchingSections}
              />
            )}
            {!isViewingMappings && !editModeField && !showForm && (
              <ManageGrid.Body.EmptyForm>
                {!!fieldsets?.length && (
                  <>
                    <Text as="h2" compact>
                      Select an Input Schema from the list
                    </Text>
                    <Text as="h3" color="grey" compact>
                      Or
                    </Text>
                  </>
                )}
                <Button
                  variant="secondary-alt"
                  size="lg"
                  icon
                  onClick={(): void => setShowForm(true)}
                >
                  <Icon name="add" />
                  Create new Input Schema
                </Button>
              </ManageGrid.Body.EmptyForm>
            )}
          </ManageGrid.Body.Form>
        </ManageGrid.Body>
      </ManageGrid>
      {isFetchingFieldsets && <PageDimmer header="" loading />}
    </div>
  );
};
